import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const ONE_DAY_MS = 86400 * 1000;

interface WelcomeCouponPopupState {
  dismissedUntil: number;
  dismiss: () => void;
}

/**
 * 신규 가입 쿠폰 팝업의 "오늘은 다시 보지 않기" 상태.
 *
 * 기존 `popup-banner-store` 와 같은 패턴이지만 저장 키를 분리한다 — 한 스토어를 공유하면
 * 다른 팝업을 닫았을 때 이 팝업까지 함께 숨겨진다.
 */
/**
 * 오늘 "다시 보지 않기" 로 닫힌 상태인지.
 *
 * 시각 비교를 컴포넌트 렌더 밖으로 뺀다 — 렌더 중 `Date.now()` 직접 호출은 순수성 규칙 위반이다.
 * (같은 이유로 `constants/promo-banner.ts` 의 `isPromoBannerActive` 도 이 형태다.)
 */
export const isDismissedToday = (
  dismissedUntil: number,
  now: number = Date.now(),
): boolean => dismissedUntil > now;

export const useWelcomeCouponPopupStore = create<WelcomeCouponPopupState>()(
  persist(
    (set) => ({
      dismissedUntil: 0,
      dismiss: () => set({ dismissedUntil: Date.now() + ONE_DAY_MS }),
    }),
    {
      name: "welcome-coupon-popup",
      partialize: (state) => ({ dismissedUntil: state.dismissedUntil }),
    },
  ),
);

interface WelcomeCouponSessionState {
  /** 팝업의 [Close] 를 눌렀는지. */
  popupClosed: boolean;
  /** 미니 배지의 [×] 를 눌렀는지. */
  badgeDismissed: boolean;
  closePopup: () => void;
  dismissBadge: () => void;
}

/**
 * 신규 가입 쿠폰 노출 표면(팝업 + 미니 배지)의 **이번 방문(탭) 한정** 상태.
 *
 * 위 `useWelcomeCouponPopupStore`(localStorage, "오늘 하루 안 보기")와 저장소를 나눈다 —
 * 한 저장소에 섞으면 수명이 다른 두 의사(이번 방문 vs 24시간)가 서로를 덮어쓴다.
 *
 * **sessionStorage 인 이유**: 팝업의 [Close] 는 원래 컴포넌트 `useState` 였는데, 그러면
 * (1) 다른 페이지의 배지가 "팝업이 닫혔는지" 를 알 수 없고 (2) 로그인 왕복·새로고침 한 번에
 * 되살아난다. 반대로 localStorage 로 올리면 "이번 방문만" 이라는 약속을 깬다.
 * sessionStorage 는 탭을 닫으면 비고 새로고침은 견뎌서 "이번 방문" 의미와 정확히 맞는다.
 * (SSR 에서는 `createJSONStorage` 가 예외를 삼키고 undefined 를 반환해 persist 가 무시된다.)
 */
export const useWelcomeCouponSessionStore = create<WelcomeCouponSessionState>()(
  persist(
    (set) => ({
      popupClosed: false,
      badgeDismissed: false,
      closePopup: () => set({ popupClosed: true }),
      // 배지 [×] 는 팝업도 함께 억제한다. 두 표면은 같은 캠페인·같은 CTA 라 "이번 방문엔
      // 그만" 이라는 의사는 한쪽에만 적용될 수 없다. 배지만 껐다가 메인으로 돌아오면
      // 훨씬 큰 전폭 바텀시트가 다시 열려서, 닫을수록 더 크게 되돌아오는 꼴이 된다.
      dismissBadge: () => set({ badgeDismissed: true, popupClosed: true }),
    }),
    {
      name: "welcome-coupon-session",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
